import React, {
  useMemo, useEffect, useState, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Container, Card } from 'reactstrap';
import { toast } from 'react-toastify';
/* eslint-disable import/no-extraneous-dependencies */
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
/* eslint-enable import/no-extraneous-dependencies */
import placeApi from 'api/placeApi';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import AppButton from 'components/App/Button';
import Loading from 'components/App/Loading';
import { apiErrorHandler } from 'utils';
import contractImg from 'assets/images/image 9.svg';
import styles from './Contract.module.scss';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const Contract = (props) => {
  const {
    isLoadingGetPlaceDetail,
    placeDetail,
    actionGetPlaceDetail,
    actionResetPlaceDetail,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isUploadingContract, setIsUploadingContract] = useState(false);

  const breadcrumbList = useMemo(() => [
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `/locations/detail?place=${placeId}`,
    },
    {
      name: 'Hợp đồng',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({
        placeId,
      });
    }

    return () => {
      actionResetPlaceDetail();
    };
  }, [actionGetPlaceDetail, actionResetPlaceDetail, placeId]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [placeDetail?.contract]);

  useEffect(() => {
    const contractUrl = placeDetail?.contract;
    const container = containerRef.current;

    if (!contractUrl || !container || containerWidth <= 0) {
      return undefined;
    }

    let isCancelled = false;
    setIsLoadingPdf(true);
    container.innerHTML = '';

    const renderPdf = async () => {
      try {
        const pdf = await pdfjsLib.getDocument(contractUrl).promise;

        if (isCancelled) return;

        const pages = await Promise.all(
          Array.from({ length: pdf.numPages }, (_, index) => pdf.getPage(index + 1)),
        );

        if (isCancelled) return;

        const pageCanvases = pages.map((page) => {
          const viewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.className = styles.ContractPage;
          const context = canvas.getContext('2d');

          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;

          container.appendChild(canvas);

          return {
            page,
            canvasContext: context,
            viewport: scaledViewport,
          };
        });

        await Promise.all(
          pageCanvases.map(({ page, canvasContext, viewport }) => page.render({
            canvasContext,
            viewport,
          }).promise),
        );
      } catch (error) {
        if (!isCancelled) {
          container.innerHTML = '<p class="text-center text-muted py-4">Không thể tải file PDF</p>';
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingPdf(false);
        }
      }
    };

    renderPdf();

    return () => {
      isCancelled = true;
    };
  }, [placeDetail?.contract, containerWidth]);

  const handleSelectPdf = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    const isPdf = selectedFile.type === 'application/pdf'
      || selectedFile.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      toast.error('Vui lòng chọn file PDF');
      return;
    }

    if (!placeId) {
      toast.error('Không tìm thấy thông tin chỗ nghỉ');
      return;
    }

    try {
      setIsUploadingContract(true);
      await placeApi.uploadPlaceContract({
        placeId,
        file: selectedFile,
      });
      toast.success('Tải lên hợp đồng thành công!');
      actionGetPlaceDetail({ placeId });
    } catch (error) {
      apiErrorHandler(error);
    } finally {
      setIsUploadingContract(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [actionGetPlaceDetail, placeId]);

  return (
    <div className="page-content">
      <Loading
        loaderOnPage
        loading={isLoadingGetPlaceDetail || isLoadingPdf || isUploadingContract}
      />
      <Meta name="Hợp đồng với Bookese" />

      <Container fluid>
        <Breadcrumb
          pageTitle="HỢP ĐỒNG VỚI BOOKESE"
          breadcrumbList={breadcrumbList}
        />

        <div className={styles.Page}>
          <Card className={styles.CardWrapper}>
            <div className={styles.flex}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className={styles.FileInput}
                onChange={handleFileChange}
              />
              <AppButton
                type="button"
                classes={styles.BtnDownload}
                onClick={handleSelectPdf}
              >
                <i className="bx bx-download font-size-16 align-middle me-2" />
                Tải file PDF
              </AppButton>
            </div>

            <div ref={containerRef} className={styles.ImageContainer}>
              {!placeDetail?.contract && (
                <img
                  src={contractImg}
                  alt="Hợp đồng Bookese"
                  className={styles.ContractImage}
                />
              )}
            </div>
          </Card>
        </div>
      </Container>
    </div>
  );
};

Contract.propTypes = {
  isLoadingGetPlaceDetail: PropTypes.bool,
  placeDetail: PropTypes.instanceOf(Object),
  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionResetPlaceDetail: PropTypes.func.isRequired,
};

Contract.defaultProps = {
  isLoadingGetPlaceDetail: false,
  placeDetail: {},
};

export default Contract;
